import * as React from "react";
import { NavigationMenu } from "radix-ui";
import cn from "classnames";

import { Typography } from "storefront/ui/typography";
import { MENSWEAR, WOMENSWEAR } from "storefront/Department";
import navigationClicked from "storefront/Analytics/EventCreators/navigationClicked";
import useAnalytics from "storefront/hooks/useAnalytics";
import CaretIcon from "./components/CaretIcon";

import DesignersDropdown from "./components/DesignersDropdown";
import CategoryDropdown from "./components/CategoryDropdown";

import styles from "./MerchandisingMenu.module.scss";

const NAV_ITEM_IDS = {
  designers: "designers",
  cybersale: "cybersale",
  holidaygifts: "holidaygifts",
  staffpicks: "staffpicks",
  sneakers: "sneakers",
  collections: "collections",
  editorial: "editorial",
  menswear: "menswear",
  womenswear: "womenswear",
};

type BaseNavigationMenuContentProps = React.ComponentProps<
  typeof NavigationMenu.Content
> & {
  active: boolean;
};

const ClickOnlyNavigationMenuTrigger = React.forwardRef<
  React.ElementRef<typeof NavigationMenu.Trigger>,
  React.ComponentPropsWithoutRef<typeof NavigationMenu.Trigger>
>((props, ref) => (
  <NavigationMenu.Trigger
    ref={ref}
    {...props}
    onPointerEnter={(event) => event.preventDefault()}
    onPointerMove={(event) => event.preventDefault()}
    onPointerLeave={(event) => event.preventDefault()}
  />
));
ClickOnlyNavigationMenuTrigger.displayName = NavigationMenu.Trigger.displayName;

const NavigationMenuContent = ({
  key,
  forceMount: _,
  className,
  active,
  ...rest
}: BaseNavigationMenuContentProps) => {
  return (
    <NavigationMenu.Content
      key={String(key)}
      forceMount
      {...rest}
      className={cn(className, {
        [styles.hidden]: !active,
      })}
    />
  );
};

const MerchandisingMenu = () => {
  const [value, setValue] = React.useState("");
  const { track } = useAnalytics();

  return (
    <NavigationMenu.Root
      value={value}
      onValueChange={setValue}
      className={styles.root}
    >
      <NavigationMenu.List className={styles.list}>
        <NavigationMenu.Item value={NAV_ITEM_IDS.designers}>
          <ClickOnlyNavigationMenuTrigger
            className={cn(styles.item, styles.trigger)}
          >
            <Typography variant="title-sm">DESIGNERS</Typography>{" "}
            <CaretIcon className={styles.caret} aria-hidden="true" />
          </ClickOnlyNavigationMenuTrigger>
          <NavigationMenuContent
            active={value === NAV_ITEM_IDS.designers}
            className={styles.content}
          >
            <DesignersDropdown />
          </NavigationMenuContent>
        </NavigationMenu.Item>
        <NavigationMenu.Item value={NAV_ITEM_IDS.menswear}>
          <ClickOnlyNavigationMenuTrigger
            className={cn(styles.item, styles.trigger)}
          >
            <Typography variant="title-sm">MENSWEAR</Typography>{" "}
            <CaretIcon className={styles.caret} aria-hidden="true" />
          </ClickOnlyNavigationMenuTrigger>
          <NavigationMenuContent
            active={value === NAV_ITEM_IDS.menswear}
            className={styles.content}
          >
            <CategoryDropdown department={MENSWEAR} />
          </NavigationMenuContent>
        </NavigationMenu.Item>
        <NavigationMenu.Item value={NAV_ITEM_IDS.womenswear}>
          <ClickOnlyNavigationMenuTrigger
            className={cn(styles.item, styles.trigger)}
          >
            <Typography variant="title-sm">WOMENSWEAR</Typography>{" "}
            <CaretIcon className={styles.caret} aria-hidden="true" />
          </ClickOnlyNavigationMenuTrigger>
          <NavigationMenuContent
            active={value === NAV_ITEM_IDS.womenswear}
            className={styles.content}
          >
            <CategoryDropdown department={WOMENSWEAR} />
          </NavigationMenuContent>
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <NavigationMenu.Link
            onClick={() => {
              track(navigationClicked("vintage", "global_header"));
            }}
            className={styles.item}
            href="/browse/vintage"
          >
            <Typography variant="title-sm">VINTAGE</Typography>
          </NavigationMenu.Link>
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <NavigationMenu.Link
            onClick={() => {
              track(navigationClicked("sneakers", "global_header"));
            }}
            className={styles.item}
            href="/categories/sneakers"
          >
            <Typography variant="title-sm">SNEAKERS</Typography>
          </NavigationMenu.Link>
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <NavigationMenu.Link
            onClick={() => {
              track(navigationClicked("staff_picks", "global_header"));
            }}
            className={styles.item}
            href="/shop/staff-picks"
          >
            <Typography variant="title-sm">STAFF PICKS</Typography>
          </NavigationMenu.Link>
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <NavigationMenu.Link
            onClick={() => {
              track(navigationClicked("collections", "global_header"));
            }}
            className={styles.item}
            href="/collections"
          >
            <Typography variant="title-sm">COLLECTIONS</Typography>
          </NavigationMenu.Link>
        </NavigationMenu.Item>
        <NavigationMenu.Item>
          <NavigationMenu.Link
            onClick={() => {
              track(navigationClicked("editorial", "global_header"));
            }}
            className={styles.item}
            href="/drycleanonly"
          >
            <Typography variant="title-sm">EDITORIAL</Typography>
          </NavigationMenu.Link>
        </NavigationMenu.Item>
      </NavigationMenu.List>
      <div className={styles.viewportContainer}>
        <NavigationMenu.Viewport
          onPointerEnter={(event) => event.preventDefault()}
          onPointerMove={(event) => event.preventDefault()}
          onPointerLeave={(event) => event.preventDefault()}
          className={styles.viewport}
        />
      </div>
    </NavigationMenu.Root>
  );
};

export default MerchandisingMenu;
